<script setup lang="ts">
/**
 * List and detail side by side — groups, companies, audit entries, managers
 * with a record list on the left and the chosen record on the right. The list
 * pane has a head (`list-header`: title, search), the list itself (default
 * slot or `list`), an empty state (`list-empty`, shown while `listEmpty`) and
 * a `list-footer`. The detail pane shows `detail-header` + `detail` while
 * `hasSelection`, else its empty state (`detail-empty` or `emptyTitle` /
 * `emptyText`).
 *
 * It measures its own width (not the viewport): below `stackBelow` px only one
 * pane shows — the list, or the detail once something is selected, with a
 * back button that emits `back` (the caller clears its selection).
 */
import ArrowLeft from 'lucide-vue-next/dist/esm/icons/arrow-left.js'; import MousePointerClick from 'lucide-vue-next/dist/esm/icons/mouse-pointer-click.js';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { NxButton } from '../Button';

const props = withDefaults(
  defineProps<{
    /** Something is selected: the detail pane shows it (and wins when stacked). */
    hasSelection: boolean;
    /** The list has no entries: shows the `list-empty` slot instead of the list. */
    listEmpty?: boolean;
    /** Width of the list pane when side by side. */
    listWidth?: 'sm' | 'md' | 'lg';
    /** Below this width (px) the panes stack and show one at a time. */
    stackBelow?: number;
    listLabel?: string;
    detailLabel?: string;
    backLabel?: string;
    emptyTitle?: string;
    emptyText?: string;
  }>(),
  {
    listEmpty: false,
    listWidth: 'md',
    stackBelow: 720,
    listLabel: 'Liste',
    detailLabel: 'Details',
    backLabel: 'Zurück zur Liste',
    emptyTitle: 'Nichts ausgewählt',
    emptyText: 'Links einen Eintrag wählen, um ihn hier zu sehen.',
  }
);

const emit = defineEmits<{ back: [] }>();

const rootRef = useTemplateRef<HTMLElement>('root');
const width = ref(Number.POSITIVE_INFINITY);
let observer: ResizeObserver | null = null;
onMounted(() => {
  if (typeof ResizeObserver === 'undefined' || !rootRef.value) return;
  observer = new ResizeObserver((entries) => {
    const w = entries[0]?.contentRect.width;
    if (w && w > 0) width.value = w;
  });
  observer.observe(rootRef.value);
});
onBeforeUnmount(() => observer?.disconnect());

const stacked = computed(() => width.value < props.stackBelow);
const showList = computed(() => !stacked.value || !props.hasSelection);
const showDetail = computed(() => !stacked.value || props.hasSelection);
</script>

<template>
  <div
    ref="root"
    class="nx-master-detail"
    :class="[`list-${props.listWidth}`, { 'is-stacked': stacked }]"
    data-testid="nx-master-detail"
  >
    <section
      v-show="showList"
      class="nx-master-detail__pane nx-master-detail__list"
      :aria-label="props.listLabel"
      data-testid="nx-master-detail-list"
    >
      <header v-if="$slots['list-header']" class="nx-master-detail__list-head">
        <slot name="list-header" />
      </header>
      <div class="nx-master-detail__list-body">
        <div v-if="props.listEmpty && $slots['list-empty']" class="nx-master-detail__list-empty">
          <slot name="list-empty" />
        </div>
        <template v-else>
          <slot name="list"><slot /></slot>
        </template>
      </div>
      <footer v-if="$slots['list-footer']" class="nx-master-detail__list-foot">
        <slot name="list-footer" />
      </footer>
    </section>

    <section
      v-show="showDetail"
      class="nx-master-detail__pane nx-master-detail__detail"
      :aria-label="props.detailLabel"
      data-testid="nx-master-detail-detail"
    >
      <template v-if="props.hasSelection">
        <div v-if="stacked" class="nx-master-detail__back">
          <NxButton
            height="s"
            variant="ghost"
            data-testid="nx-master-detail-back"
            @click="emit('back')"
          >
            <ArrowLeft class="nxt-icon-sm" aria-hidden="true" />
            {{ props.backLabel }}
          </NxButton>
        </div>
        <header v-if="$slots['detail-header']" class="nx-master-detail__hero">
          <slot name="detail-header" />
        </header>
        <div class="nx-master-detail__detail-body">
          <slot name="detail" />
        </div>
      </template>
      <div v-else class="nx-master-detail__empty" data-testid="nx-master-detail-empty">
        <slot name="detail-empty">
          <span class="nx-master-detail__empty-icon" aria-hidden="true">
            <MousePointerClick class="nxt-icon-lg" />
          </span>
          <strong class="nx-master-detail__empty-title">{{ props.emptyTitle }}</strong>
          <span v-if="props.emptyText" class="nx-master-detail__empty-text">
            {{ props.emptyText }}
          </span>
        </slot>
      </div>
    </section>
  </div>
</template>

<style scoped>
.nx-master-detail {
  --md-list-width: var(--nxt-panel-width-xl);

  display: grid;
  grid-template-columns: minmax(0, var(--md-list-width)) minmax(0, 1fr);
  gap: var(--nxt-space-md);
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.nx-master-detail.list-sm {
  --md-list-width: var(--nxt-panel-width-lg);
}
.nx-master-detail.list-lg {
  --md-list-width: var(--nxt-panel-width-2xl);
}
.nx-master-detail.is-stacked {
  grid-template-columns: minmax(0, 1fr);
}
.nx-master-detail__pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
}
.nx-master-detail__list-head {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-md) var(--nxt-space-md) var(--nxt-space-sm);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-master-detail__list-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-height: 0;
  padding: var(--nxt-space-xs);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.nx-master-detail__list-empty {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  padding: var(--nxt-space-lg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
.nx-master-detail__list-foot {
  flex: 0 0 auto;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-master-detail__back {
  flex: 0 0 auto;
  padding: var(--nxt-space-xs) var(--nxt-space-sm) 0;
}
.nx-master-detail__hero {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-md) var(--nxt-space-lg);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--nxt-brand-subtle-bg) 55%, transparent),
    transparent
  );
}
.nx-master-detail__detail-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-md);
  min-height: 0;
  padding: var(--nxt-space-md) var(--nxt-space-lg);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.nx-master-detail__empty {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xl) var(--nxt-space-md);
  text-align: center;
}
.nx-master-detail__empty-icon {
  display: grid;
  place-items: center;
  width: var(--nxt-control-size-xl);
  height: var(--nxt-control-size-xl);
  margin-block-end: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-xl);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-master-detail__empty-title {
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-master-detail__empty-text {
  max-width: var(--nxt-panel-width-lg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
</style>
